import countries from 'world-countries'

// Create a comprehensive mapping of country names to country codes
const createCountryCodeMap = () => {
  const countryMap = new Map()

  countries.forEach(country => {
    // Add common name
    if (country.name?.common) {
      countryMap.set(country.name.common.toLowerCase(), {
        code: country.cca2.toLowerCase(),
        flag: country.flag,
        name: country.name.common
      })
    }

    // Add official name
    if (country.name?.official && country.name.official !== country.name.common) {
      countryMap.set(country.name.official.toLowerCase(), {
        code: country.cca2.toLowerCase(),
        flag: country.flag,
        name: country.name.common
      })
    }

    // Add alternative spellings
    if (country.altSpellings) {
      country.altSpellings.forEach(alt => {
        countryMap.set(alt.toLowerCase(), {
          code: country.cca2.toLowerCase(),
          flag: country.flag,
          name: country.name.common
        })
      })
    }

    // Add common variations
    const variations = getCountryVariations(country.name.common)
    variations.forEach(variation => {
      countryMap.set(variation.toLowerCase(), {
        code: country.cca2.toLowerCase(),
        flag: country.flag,
        name: country.name.common
      })
    })
  })

  return countryMap
}

// Get common variations of country names
const getCountryVariations = (countryName) => {
  const variations = []

  // Common replacements
  const replacements = [
    // US variations
    ['United States of America', 'United States'],
    ['USA', 'United States'],
    ['US', 'United States'],
    // UK variations
    ['United Kingdom of Great Britain and Northern Ireland', 'United Kingdom'],
    ['UK', 'United Kingdom'],
    ['Great Britain', 'United Kingdom'],
    ['Britain', 'United Kingdom'],
    // UAE variations
    ['United Arab Emirates', 'UAE'],
    // South Korea
    ['Korea, Republic of', 'South Korea'],
    ['Republic of Korea', 'South Korea'],
    // North Korea
    ['Korea, Democratic People\'s Republic of', 'North Korea'],
    ['Democratic People\'s Republic of Korea', 'North Korea'],
    // Russia
    ['Russian Federation', 'Russia'],
    // Iran
    ['Iran, Islamic Republic of', 'Iran'],
    ['Islamic Republic of Iran', 'Iran'],
    // Venezuela
    ['Venezuela, Bolivarian Republic of', 'Venezuela'],
    // Syria
    ['Syrian Arab Republic', 'Syria'],
    // Vietnam
    ['Viet Nam', 'Vietnam'],
    // Congo
    ['Congo, The Democratic Republic of the', 'Democratic Republic of the Congo'],
    // Moldova
    ['Moldova, Republic of', 'Moldova'],
    // Tanzania
    ['Tanzania, United Republic of', 'Tanzania'],
    // Macedonia
    ['Macedonia, The Former Yugoslav Republic of', 'North Macedonia'],
    ['Former Yugoslav Republic of Macedonia', 'North Macedonia']
  ]

  // Add the country name itself
  variations.push(countryName)

  // Apply replacements
  replacements.forEach(([from, to]) => {
    if (countryName.includes(from)) {
      variations.push(countryName.replace(from, to))
    }
    if (from === countryName) {
      variations.push(to)
    }
    if (to === countryName) {
      variations.push(from)
    }
  })

  return variations
}

// Global country code map
const countryCodeMap = createCountryCodeMap()

/**
 * Get country information by country name
 * @param {string} countryName - The name of the country
 * @returns {object|null} - Country info with code, flag, and standardized name
 */
export const getCountryInfo = (countryName) => {
  if (!countryName || typeof countryName !== 'string') {
    return null
  }

  // Try exact match first
  const exactMatch = countryCodeMap.get(countryName.toLowerCase())
  if (exactMatch) {
    return exactMatch
  }

  // Try partial matches for cases where API returns different formats
  const partialMatch = Array.from(countryCodeMap.entries()).find(([key]) => {
    const lowerCountryName = countryName.toLowerCase()
    const lowerKey = key.toLowerCase()

    // Avoid false matches for very short strings
    if (lowerCountryName.length < 3 || lowerKey.length < 3) {
      return false
    }

    return key.includes(lowerCountryName) || lowerCountryName.includes(key)
  })

  if (partialMatch) {
    return partialMatch[1]
  }

  // Fallback: generate a code from first two letters
  return {
    code: countryName.slice(0, 2).toLowerCase(),
    flag: null,
    name: countryName
  }
}

/**
 * Get country code by country name
 * @param {string} countryName - The name of the country
 * @returns {string} - Two-letter country code (lowercase)
 */
export const getCountryCode = (countryName) => {
  const info = getCountryInfo(countryName)
  return info?.code || countryName?.slice(0, 2).toLowerCase() || 'us'
}

/**
 * Get country flag emoji by country name
 * @param {string} countryName - The name of the country
 * @returns {string|null} - Flag emoji or null
 */
export const getCountryFlag = (countryName) => {
  const info = getCountryInfo(countryName)
  return info?.flag || null
}

/**
 * Get all countries with their codes and flags
 * @returns {Array} - Array of country objects
 */
export const getAllCountries = () => {
  return countries.map(country => ({
    name: country.name.common,
    code: country.cca2.toLowerCase(),
    flag: country.flag,
    region: country.region,
    subregion: country.subregion
  }))
}